import { baseApi } from 'services/api';
import { API_TAG_TYPES, API_VERSION } from 'constants/common';
import {
  FsmCustomerPortalSessionResponse,
  FsmSubscriptionConfirmResponse,
  FsmSubscriptionDetailResponse,
  FsmSubscriptionSetupResponse,
  FsmSubscriptionUpdatePaymentMethodPayload,
  FsmSubscriptionUpdatePaymentMethodResponse,
  FsmV2SubscriptionConfirmPayload,
  FsmV2SubscriptionUpdatePayload,
  ListSubscriptionPlansPayload,
  SubscriptionPlan
} from './fsmSubscription.model';
import { TeamIdPayload } from 'containers/home/home.model';
import {
  setLoading,
  setLoadingDetails,
  setSubscriptionDetails,
  setSubscriptionPlans
} from './fsmSubscriptionPlansSlice';

const fsmIntegrationApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getSetupIntent: builder.mutation<FsmSubscriptionSetupResponse, TeamIdPayload>({
      query: ({ teamId }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/fsm-subscription/setup`,
        method: 'POST'
      }),
      extraOptions: {
        failure: 'Failed to setup Field Serve subscription',
        showToast: true
      }
    }),
    updatePaymentMethod: builder.mutation<
      FsmSubscriptionUpdatePaymentMethodResponse,
      FsmSubscriptionUpdatePaymentMethodPayload
    >({
      query: ({ teamId, paymentMethodId }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/fsm-subscription/payment-method`,
        method: 'PATCH',
        body: { paymentMethodId }
      }),
      invalidatesTags: [API_TAG_TYPES.FSM_SUBSCRIPTION],
      extraOptions: {
        failure: 'Failed to update payment method',
        showToast: true
      }
    }),
    createCustomerPortalSession: builder.mutation<FsmCustomerPortalSessionResponse, TeamIdPayload>({
      query: ({ teamId }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/fsm-subscription/customer-portal-session`,
        method: 'POST'
      }),
      extraOptions: {
        failure: 'Failed to create customer portal session',
        showToast: true
      }
    }),
    listSubscriptionPlans: builder.query<SubscriptionPlan[], ListSubscriptionPlansPayload>({
      query: ({ teamId }) => ({
        url: `${API_VERSION.V1}/subscription/plans?teamId=${teamId}&version=web`,
        method: 'GET'
      }),
      providesTags: [API_TAG_TYPES.LIST_SUBSCRIPTION],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          dispatch(setLoading(true));
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(setSubscriptionPlans(data));
          }
        } catch (error) {
          //
        } finally {
          dispatch(setLoading(false));
        }
      },
      extraOptions: {
        failure: 'Failed to fetch subscription plans',
        showToast: false
      }
    }),
    confirmFsmV2Subscription: builder.mutation<FsmSubscriptionConfirmResponse, FsmV2SubscriptionConfirmPayload>({
      query: ({ teamId, body }) => ({
        url: `${API_VERSION.V2}/teams/${teamId}/fsm-subscription/confirm`,
        method: 'POST',
        body
      }),
      extraOptions: {
        success: 'Field Serve subscription confirmed successfully',
        failure: 'Failed to confirm Field Serve subscription',
        showToast: false
      },
      invalidatesTags: [API_TAG_TYPES.FSM_SUBSCRIPTION, API_TAG_TYPES.LIST_SUBSCRIPTION]
    }),
    updateFsmV2Subscription: builder.mutation<{ message: string }, FsmV2SubscriptionUpdatePayload>({
      query: ({ teamId, body }) => ({
        url: `${API_VERSION.V2}/teams/${teamId}/fsm-subscription/update`,
        method: 'PATCH',
        body
      }),
      extraOptions: {
        success: 'Field Serve subscription updated successfully',
        failure: 'Failed to update Field Serve subscription',
        showToast: true
      },
      invalidatesTags: [API_TAG_TYPES.FSM_SUBSCRIPTION]
    }),
    getFsmSubscription: builder.query<FsmSubscriptionDetailResponse, TeamIdPayload>({
      query: ({ teamId }) => ({
        url: `${API_VERSION.V1}/teams/${teamId}/fsm-subscription`,
        method: 'GET'
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          dispatch(setLoadingDetails(true));
          const { data } = await queryFulfilled;
          if (data) {
            dispatch(setSubscriptionDetails(data));
          }
        } catch (error) {
          //
        } finally {
          dispatch(setLoadingDetails(false));
        }
      },
      providesTags: [API_TAG_TYPES.FSM_SUBSCRIPTION],
      extraOptions: {
        failure: 'Failed to fetch Field Serve subscription details',
        showToast: false
      }
    })
  })
});

export const {
  useGetSetupIntentMutation,
  useUpdatePaymentMethodMutation,
  useCreateCustomerPortalSessionMutation,
  useListSubscriptionPlansQuery,
  useLazyListSubscriptionPlansQuery,
  useConfirmFsmV2SubscriptionMutation,
  useUpdateFsmV2SubscriptionMutation,
  useGetFsmSubscriptionQuery,
  useLazyGetFsmSubscriptionQuery
} = fsmIntegrationApi;
